import type { Container, MaskData } from 'pixi.js';
import type { Subscene } from '../core/scene/Subscene';
import type { Scene } from '../core/scene/Scene';
import type { ColorChannelMapFilter, DuoToneFilter, Filter } from '../core/filters';
import type { TextAsset } from '../core/asset/TextAsset';
import type { TextAssetStyle } from '../core/asset/TextAsset/TextAssetStyle';
import type { TemplateBundle } from '../types';
import type { OpaqueVideoData } from '../video-data/types';
import type { AssetGroup } from '../core/asset/AssetGroup';
import type { CircleAsset } from '../core/asset/CircleAsset';
import type { ColorAsset } from '../core/asset/ColorAsset';
import type { GradientAsset } from '../core/asset/GradientAsset';
import type { ChartAsset } from '../core/asset/ChartAsset';
import type { HtmlAsset } from '../core/asset/HtmlAsset';
import type { IconAsset } from '../core/asset/IconAsset';
import type { LottieAsset } from '../core/asset/LottieAsset/LottieAsset';
import type { RectangleAsset } from '../core/asset/RectangleAsset';
import type { Asset } from '../core/asset/BaseAsset';
import type { TimeAwareMediaAsset } from '../core/asset/TimeAwareMediaAsset';
import {
    AnimationCreationSource,
    DynamicStylingCreationLevel,
    SeekMode,
    LicenseEnum,
    WatermarkPositions,
    AssetCreatedFrom,
    SceneImageRenderMode,
    TextDirection,
} from '../enums';
import type { FancyCaptionAsset } from '../core/asset/FancyCaptionAsset/asset/FancyCaptionAsset';
import type { CaptionAsset } from '../core/asset/CaptionAsset/asset/CaptionAsset';
import type { SceneGroup } from '../core/SceneGroup';
import type { CompositingAsset } from '../core/CompositingAsset';
import type { AudioAsset } from '../audio/AudioAsset';
import type { StickerAsset } from '../core/asset/types';
import {
    AspectRatio,
    ALL_ASPECT_RATIOS as _ALL_ASPECT_RATIOS,
    SUPPORTED_VIDEO_HEIGHTS as _SUPPORTED_VIDEO_HEIGHTS,
    BASE_SLIDE_DIMENSIONS as _BASE_SLIDE_DIMENSIONS,
} from '../aspectRatioConfig';
import type { CircleMask } from '../core/mask/CircleMask';
import type { StencilMask } from '../core/mask/StencilMask';
import type { PolygonMask } from '../core/mask/PolygonMask';

/**
 *  The Config class exposes a set of constants and utilities for valid video-data options.
 */
export class Config {
    /**
     * IMPORTANT
     * All methods & variables here are exposed externally (via the "API") so any changes require ensuring
     * that any code using VideoData (in Luminary and outside) have been updated!
     */
    /* 1px font-metric slack: rendered text runs marginally taller than textSize * (1 + leadingMultiplier),
       so a box sized to exactly one line would clip it. Shared with web's resize-floor math. */
    static LINE_HEIGHT_RENDER_SLACK = 1;

    /* graphic types */
    static BOX_GRAPHIC = 'box' as const;
    static CIRCLE_GRAPHIC = 'circle' as const;
    static BOX_BORDER_GRAPHIC = 'boxBorder' as const;

    static ALL_GRAPHICS = [Config.BOX_GRAPHIC, Config.CIRCLE_GRAPHIC, Config.BOX_BORDER_GRAPHIC];

    static ALL_CREATION_LEVELS = Object.values(DynamicStylingCreationLevel);

    /* zig zag start options */
    static ZIG_ZAG_START_LEFT = 'left';
    static ZIG_ZAG_START_RIGHT = 'right';

    static ALL_ZIG_ZAG_START_OPTIONS = [Config.ZIG_ZAG_START_LEFT, Config.ZIG_ZAG_START_RIGHT];

    /* scale anchor options */
    static SCALE_ANCHOR_TOP_LEFT = 'topLeft' as const;
    static SCALE_ANCHOR_CENTER = 'center' as const;

    static ALL_SCALE_ANCHOR_OPTIONS = [
        Config.SCALE_ANCHOR_TOP_LEFT,
        Config.SCALE_ANCHOR_CENTER,
    ] as const;

    /* inferred property options */
    static INFERRED_MAX_HEIGHT = 'maxHeight';
    static INFERRED_HEIGHT = 'height';
    static INFERRED_MAX_WIDTH = 'maxWidth';
    static INFERRED_HEIGHT_GAPLESS = 'gapless';
    static INFERRED_WIDTH = 'width';

    static ALL_INFERRED_WIDTH = [Config.INFERRED_MAX_WIDTH, Config.INFERRED_WIDTH];

    static ALL_INFERRED_HEIGHT = [
        Config.INFERRED_MAX_HEIGHT,
        Config.INFERRED_HEIGHT,
        Config.INFERRED_HEIGHT_GAPLESS,
    ];

    static ALL_INFERRED = [
        Config.INFERRED_MAX_HEIGHT,
        Config.INFERRED_HEIGHT,
        Config.INFERRED_MAX_WIDTH,
        Config.INFERRED_WIDTH,
        Config.INFERRED_HEIGHT_GAPLESS,
    ];

    /* interpolation presets */
    static readonly MIRRORED_INTERPOLATION_PREFIX = 'mirrored_';
    static readonly LINEAR_INTERPOLATION_PRESET = 'linear';
    static readonly MIRRORED_LINEAR_INTERPOLATION_PRESET =
        Config.MIRRORED_INTERPOLATION_PREFIX + Config.LINEAR_INTERPOLATION_PRESET;
    static readonly EASE_IN_INTERPOLATION_PRESET = 'ease_in' as const;
    static readonly MIRRORED_EASE_IN_INTERPOLATION_PRESET =
        Config.MIRRORED_INTERPOLATION_PREFIX + Config.EASE_IN_INTERPOLATION_PRESET;
    static readonly EASE_OUT_INTERPOLATION_PRESET = 'ease_out' as const;
    static readonly MIRRORED_EASE_OUT_INTERPOLATION_PRESET =
        Config.MIRRORED_INTERPOLATION_PREFIX + Config.EASE_OUT_INTERPOLATION_PRESET;
    static readonly EASE_IN_OUT_INTERPOLATION_PRESET = 'ease_in_out' as const;
    static readonly MIRRORED_EASE_IN_OUT_INTERPOLATION_PRESET =
        Config.MIRRORED_INTERPOLATION_PREFIX + Config.EASE_IN_OUT_INTERPOLATION_PRESET;
    static readonly BOUNCE_INTERPOLATION_PRESET = 'bounce' as const;
    static readonly MIRRORED_BOUNCE_INTERPOLATION_PRESET =
        Config.MIRRORED_INTERPOLATION_PREFIX + Config.BOUNCE_INTERPOLATION_PRESET;
    static readonly STEP_INTERPOLATION_PRESET = 'step' as const;
    static readonly MIRRORED_STEP_INTERPOLATION_PRESET =
        Config.MIRRORED_INTERPOLATION_PREFIX + Config.STEP_INTERPOLATION_PRESET;

    static ALL_INTERPOLATION_PRESETS = [
        Config.LINEAR_INTERPOLATION_PRESET,
        Config.EASE_IN_INTERPOLATION_PRESET,
        Config.EASE_OUT_INTERPOLATION_PRESET,
        Config.EASE_IN_OUT_INTERPOLATION_PRESET,
        Config.BOUNCE_INTERPOLATION_PRESET,
        Config.STEP_INTERPOLATION_PRESET,
    ] as const;

    // Interpolations offered in the scene-design editor dropdowns. 'step' is valid (the match-cut
    // alpha-cut applies it programmatically and the @StringAttribute validator still accepts it) but is
    // not hand-pickable — a mid-animation jump reads as wrong on a normal scale/position/opacity tween.
    static SELECTABLE_INTERPOLATION_PRESETS = Config.ALL_INTERPOLATION_PRESETS.filter(
        (preset) => preset !== Config.STEP_INTERPOLATION_PRESET,
    );

    /* interpolation preset data */
    static LINEAR_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 0 }, out: { distance: 0.25, angle: 0.7853981633974483 } },
            { position: { x: 1, y: 1 }, in: { distance: 0.25, angle: -2.356194490192345 } },
        ],
    };
    static MIRRORED_LINEAR_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 1 }, out: { distance: 0.25, angle: -0.7853981633974483 } },
            { position: { x: 1, y: 0 }, in: { distance: 0.25, angle: 2.356194490192345 } },
        ],
    };

    static EASE_IN_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 0 }, out: { distance: 0.5, angle: 0 } },
            { position: { x: 1, y: 1 }, in: { distance: 0.25, angle: -2 } },
        ],
    };
    static MIRRORED_EASE_IN_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 1 }, out: { distance: 0.25, angle: -1.14157 } },
            { position: { x: 1, y: 0 }, in: { distance: 0.5, angle: 3.14159 } },
        ],
    };

    static EASE_OUT_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 0 }, out: { distance: 0.25, angle: 1.0 } },
            { position: { x: 1, y: 1 }, in: { distance: 0.5, angle: 3.141592653589793 } },
        ],
    };
    static MIRRORED_EASE_OUT_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 1 }, out: { distance: 0.5, angle: 0 } },
            { position: { x: 1, y: 0 }, in: { distance: 0.25, angle: 2.1416 } },
        ],
    };

    static EASE_IN_OUT_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 0 }, out: { distance: 0.5, angle: 0 } },
            { position: { x: 1, y: 1 }, in: { distance: 0.5, angle: 3.141592653589793 } },
        ],
    };

    static MIRRORED_EASE_IN_OUT_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 1 }, out: { distance: 0.5, angle: 0 } },
            { position: { x: 1, y: 0 }, in: { distance: 0.5, angle: 3.141592653589793 } },
        ],
    };

    static BOUNCE_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 1 }, out: { distance: 0.5, angle: 0.00463 } },
            {
                position: { x: 0.5, y: -0.375 },
                in: { distance: 0.1639, angle: 1.55451 },
                out: { distance: 0.09643, angle: 1.60772 },
            },
            { position: { x: 1, y: 0 }, in: { distance: 0.5, angle: 3.12432 } },
        ],
    };

    static MIRRORED_BOUNCE_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 0 }, out: { distance: 0.5, angle: 0 } },
            { position: { x: 1, y: 0 }, in: { distance: 0.5, angle: 3.13699 } },
        ],
    };

    // Holds the start value, then jumps to the end value near-instantly at the midpoint, then holds.
    // Used so a match-cut swaps one element for the other at the midpoint of the movement instead of
    // a crossfade where both are briefly visible. The near-vertical segment spans x 0.499..0.501.
    static STEP_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 0 }, out: { distance: 0.49, angle: 0 } },
            {
                position: { x: 0.499, y: 0 },
                in: { distance: 0.005, angle: Math.PI },
                out: { distance: 0.0005, angle: Math.PI / 2 },
            },
            {
                position: { x: 0.501, y: 1 },
                in: { distance: 0.0005, angle: -Math.PI / 2 },
                out: { distance: 0.005, angle: 0 },
            },
            { position: { x: 1, y: 1 }, in: { distance: 0.49, angle: Math.PI } },
        ],
    };

    static MIRRORED_STEP_INTERPOLATION_PRESET_DATA = {
        classType: 'BezierCurve' as const,
        points: [
            { position: { x: 0, y: 1 }, out: { distance: 0.49, angle: 0 } },
            {
                position: { x: 0.499, y: 1 },
                in: { distance: 0.005, angle: Math.PI },
                out: { distance: 0.0005, angle: -Math.PI / 2 },
            },
            {
                position: { x: 0.501, y: 0 },
                in: { distance: 0.0005, angle: Math.PI / 2 },
                out: { distance: 0.005, angle: 0 },
            },
            { position: { x: 1, y: 0 }, in: { distance: 0.49, angle: Math.PI } },
        ],
    };

    static INTERPOLATION_PRESET_DATA = {
        [Config.LINEAR_INTERPOLATION_PRESET]: Config.LINEAR_INTERPOLATION_PRESET_DATA,
        [Config.MIRRORED_LINEAR_INTERPOLATION_PRESET]:
            Config.MIRRORED_LINEAR_INTERPOLATION_PRESET_DATA,
        [Config.EASE_IN_INTERPOLATION_PRESET]: Config.EASE_IN_INTERPOLATION_PRESET_DATA,
        [Config.MIRRORED_EASE_IN_INTERPOLATION_PRESET]:
            Config.MIRRORED_EASE_IN_INTERPOLATION_PRESET_DATA,
        [Config.EASE_OUT_INTERPOLATION_PRESET]: Config.EASE_OUT_INTERPOLATION_PRESET_DATA,
        [Config.MIRRORED_EASE_OUT_INTERPOLATION_PRESET]:
            Config.MIRRORED_EASE_OUT_INTERPOLATION_PRESET_DATA,
        [Config.EASE_IN_OUT_INTERPOLATION_PRESET]: Config.EASE_IN_OUT_INTERPOLATION_PRESET_DATA,
        [Config.MIRRORED_EASE_IN_OUT_INTERPOLATION_PRESET]:
            Config.MIRRORED_EASE_IN_OUT_INTERPOLATION_PRESET_DATA,
        [Config.BOUNCE_INTERPOLATION_PRESET]: Config.BOUNCE_INTERPOLATION_PRESET_DATA,
        [Config.MIRRORED_BOUNCE_INTERPOLATION_PRESET]:
            Config.MIRRORED_BOUNCE_INTERPOLATION_PRESET_DATA,
        [Config.STEP_INTERPOLATION_PRESET]: Config.STEP_INTERPOLATION_PRESET_DATA,
        [Config.MIRRORED_STEP_INTERPOLATION_PRESET]: Config.MIRRORED_STEP_INTERPOLATION_PRESET_DATA,
    };

    /* aspect ratios */
    static LANDSCAPE_ASPECT_RATIO = AspectRatio.LANDSCAPE;
    static SQUARE_ASPECT_RATIO = AspectRatio.SQUARE;
    static VERTICAL_ASPECT_RATIO = AspectRatio.VERTICAL;
    static FOURFIVE_ASPECT_RATIO = AspectRatio.FOURFIVE;
    static ALL_ASPECT_RATIOS = _ALL_ASPECT_RATIOS;

    /* media asset size */
    static THUMBNAIL_MEDIA_SIZE = 'thumbnail_url' as const;
    static PREVIEW_MEDIA_SIZE = 'preview_url' as const;
    static RENDER_MEDIA_SIZE = 'render_url' as const;
    static ALL_MEDIA_SIZES = [
        Config.THUMBNAIL_MEDIA_SIZE,
        Config.PREVIEW_MEDIA_SIZE,
        Config.RENDER_MEDIA_SIZE,
    ];

    /* fill modes */
    static FILL_COVER = 'cover' as const;
    static FILL_CONTAIN = 'contain' as const;
    static FILL_AREA = 'area' as const;
    static ALL_FILL_MODES = [Config.FILL_COVER, Config.FILL_CONTAIN, Config.FILL_AREA];

    /* playback modes */
    static PLAYBACK_LOOP = 'loop';
    static PLAYBACK_HOLD = 'hold';
    static PLAYBACK_WAVEFORM = 'waveform';
    static ALL_PLAYBACK_MODES = [
        Config.PLAYBACK_LOOP,
        Config.PLAYBACK_HOLD,
        Config.PLAYBACK_WAVEFORM,
    ];
    static ALL_MEDIA_PLAYBACK_MODES = [Config.PLAYBACK_LOOP, Config.PLAYBACK_HOLD];

    /* lottie renderers */
    static LOTTIE_RENDERER_SVG = 'svg' as const;
    static LOTTIE_RENDERER_CANVAS = 'canvas' as const;
    static ALL_LOTTIE_RENDERERS = [Config.LOTTIE_RENDERER_SVG, Config.LOTTIE_RENDERER_CANVAS];

    /**
     * The maximum size of the lottie json file in KB. Note that this limit is somewhat
     * arbitrary and determined by trial and error. If necessary, we probably could increase
     * this in the future with some testing and tweaking of NUM_FRAMES_TO_PRECOMPUTE. The
     * tradeoff with supporting larger files is that when using the SVG renderer, generating
     * textures becomes noticeably slower.
     */
    static LOTTIE_RENDERER_SVG_MAX_FILESIZE = 50;

    /* pace multiplier */
    static PACE_MULTIPLIER_MIN = 10;
    static PACE_MULTIPLIER_MAX = 200;
    static PACE_MULTIPLIER_DEFAULT = 100;

    /* attribution */
    static CC0_LICENSE = LicenseEnum.CC0;
    static BY_LICENSE = LicenseEnum.BY;
    static USER_PRIVATE_LICENSE = LicenseEnum.USER_PRIVATE;
    static LICENSES = Object.values(LicenseEnum);

    /* background types */
    static IMAGE_TYPE_BACKGROUND = 'image' as const;
    static VIDEO_TYPE_BACKGROUND = 'video' as const;
    static ALL_BACKGROUND_TYPES = [Config.IMAGE_TYPE_BACKGROUND, Config.VIDEO_TYPE_BACKGROUND];

    /* custom transition */
    static NONE_TRANSITION_URL: string | null = null;
    static NONE_TRANSITION_DURATION = 0;

    /* min/max scene duration */
    static DEFAULT_SCENE_DURATION = 1000; // 1 sec
    static MIN_SCENE_DURATION = 1; // 1 ms
    static MAX_SCENE_DURATION = 600000; // 10 mins

    /* morph animation */
    static DEFAULT_MORPH_DURATION = 500; // 500ms

    /* recommended min/max subscene duration */
    static DEFAULT_RECOMMENDED_SUBSCENE_DURATION = 4500;
    static MIN_RECOMMENDED_SUBSCENE_DURATION = 3400;
    static MAX_RECOMMENDED_SUBSCENE_DURATION = 7000;

    /* Render output */
    static RENDER_OUTPUT_SINGLE_VIDEO = 'render_output_single_video';
    static RENDER_OUTPUT_VIDEO_PER_SLIDE = 'render_output_video_per_slide';
    static ALL_RENDER_OUTPUT_OPTIONS = [
        Config.RENDER_OUTPUT_SINGLE_VIDEO,
        Config.RENDER_OUTPUT_VIDEO_PER_SLIDE,
    ];

    /* background animation constants */
    static KENBURN_PAN_PERCENTAGE = 0.075;
    static KENBURN_ZOOM_PERCENTAGE = 0.1;
    static NONE_BACKGROUND_ANIMATION = 'none';
    static ZOOM_IN_BACKGROUND_ANIMATION = 'zoom_in';
    static ZOOM_OUT_BACKGROUND_ANIMATION = 'zoom_out';
    static PAN_UP_BACKGROUND_ANIMATION = 'pan_up';
    static PAN_DOWN_BACKGROUND_ANIMATION = 'pan_down';
    static PAN_RIGHT_BACKGROUND_ANIMATION = 'pan_right';
    static PAN_LEFT_BACKGROUND_ANIMATION = 'pan_left';
    static ALL_BACKGROUND_ANIMATIONS = [
        Config.NONE_BACKGROUND_ANIMATION,
        Config.ZOOM_IN_BACKGROUND_ANIMATION,
        Config.ZOOM_OUT_BACKGROUND_ANIMATION,
        Config.PAN_UP_BACKGROUND_ANIMATION,
        Config.PAN_DOWN_BACKGROUND_ANIMATION,
        Config.PAN_RIGHT_BACKGROUND_ANIMATION,
        Config.PAN_LEFT_BACKGROUND_ANIMATION,
    ];

    /* text positions */
    static CENTER_TEXT_POSITION = 'center';
    static TOP_RIGHT_TEXT_POSITION = 'top_right';
    static TOP_LEFT_TEXT_POSITION = 'top_left';
    static TOP_CENTER_TEXT_POSITION = 'top_center';
    static CENTER_RIGHT_TEXT_POSITION = 'center_right';
    static CENTER_LEFT_TEXT_POSITION = 'center_left';
    static BOTTOM_RIGHT_TEXT_POSITION = 'bottom_right';
    static BOTTOM_LEFT_TEXT_POSITION = 'bottom_left';
    static BOTTOM_CENTER_TEXT_POSITION = 'bottom_center';
    static ALL_TEXT_POSITIONS = [
        Config.CENTER_TEXT_POSITION,
        Config.TOP_RIGHT_TEXT_POSITION,
        Config.TOP_LEFT_TEXT_POSITION,
        Config.TOP_CENTER_TEXT_POSITION,
        Config.CENTER_RIGHT_TEXT_POSITION,
        Config.CENTER_LEFT_TEXT_POSITION,
        Config.BOTTOM_RIGHT_TEXT_POSITION,
        Config.BOTTOM_LEFT_TEXT_POSITION,
        Config.BOTTOM_CENTER_TEXT_POSITION,
    ] as const;

    /* alignment */
    static LEFT_ALIGNMENT = 'left' as const;
    static CENTER_ALIGNMENT = 'center' as const;
    static RIGHT_ALIGNMENT = 'right' as const;
    static TOP_ALIGNMENT = 'top' as const;
    static BOTTOM_ALIGNMENT = 'bottom' as const;
    static ALL_HORIZONTAL_ALIGNMENT_OPTIONS = [
        Config.LEFT_ALIGNMENT,
        Config.CENTER_ALIGNMENT,
        Config.RIGHT_ALIGNMENT,
    ];
    static ALL_VERTICAL_ALIGNMENT_OPTIONS = [
        Config.TOP_ALIGNMENT,
        Config.CENTER_ALIGNMENT,
        Config.BOTTOM_ALIGNMENT,
    ];

    /* anchor alignment */
    static TOP_LEFT_ANCHOR_ALIGNMENT = 'top_left';
    static TOP_RIGHT_ANCHOR_ALIGNMENT = 'top_right';
    static BOTTOM_LEFT_ANCHOR_ALIGNMENT = 'bottom_left';
    static BOTTOM_RIGHT_ANCHOR_ALIGNMENT = 'bottom_right';
    static ALL_ANCHOR_ALIGNMENT_OPTIONS = [
        Config.TOP_LEFT_ANCHOR_ALIGNMENT,
        Config.TOP_RIGHT_ANCHOR_ALIGNMENT,
        Config.BOTTOM_LEFT_ANCHOR_ALIGNMENT,
        Config.BOTTOM_RIGHT_ANCHOR_ALIGNMENT,
    ];

    /**
     * Returns the templateId for a given sceneDesignId.
     * For example will return "be_yourself" for the "be_yourself:text_1" input.
     */
    static getTemplateIdFromSceneDesignId(sceneDesignId: string) {
        if (sceneDesignId.indexOf(':') < 0) {
            throw new Error(
                `Expected scene design in form of <template_id>:<scene_design> but got '${sceneDesignId}'`,
            );
        }
        return sceneDesignId.split(':')[0];
    }

    /**
     * Gets the scene design name without the template prefix.
     * For example, if sceneDesignId is "noctis:outro_4b", this returns "outro_4b"
     */
    static getSceneDesignNameFromSceneDesignId(sceneDesignId: string) {
        if (sceneDesignId.indexOf(':') < 0) {
            throw new Error(
                `Expected scene design in form of <template_id>:<scene_design> but got '${sceneDesignId}'`,
            );
        }
        return sceneDesignId.split(':')[1];
    }

    /* colors */

    /**
     * Finds all references to a color reference x in the scene data, and replaces them with a new color reference y.
     */
    static replaceColorsInSceneData(
        sceneData: OpaqueVideoData,
        currentColorReferenceNumber: number,
        newColorReferenceNumber: number,
    ) {
        if (!sceneData) {
            throw new Error('Scene data cannot be null or undefined');
        }

        const sceneDataStr = JSON.stringify(sceneData);
        const searchPattern = new RegExp(`themeRef_colors_${currentColorReferenceNumber}`, 'g');

        const updatedSceneDataStr = sceneDataStr.replace(
            searchPattern,
            `themeRef_colors_${newColorReferenceNumber}`,
        );
        return JSON.parse(updatedSceneDataStr);
    }

    /* fonts */
    static DEFAULT_FONT_URL = 'https://storage.googleapis.com/lumen5-site-css/NotoSans-Bold.ttf';

    /* watermark */
    static INHERIT_FROM_TEMPLATE = 'inheritFromTemplate';
    static WATERMARK_INHERIT_TEMPLATE_KEY = 'watermark';
    static WATERMARK_NONE = 'none';
    static WATERMARK_MIN_OPACITY = 0;
    static WATERMARK_MAX_OPACITY = 1;
    static WATERMARK_DEFAULT_OPACITY = 0.4;
    static WATERMARK_TOP_LEFT_POSITION = WatermarkPositions.TopLeft;
    static WATERMARK_TOP_RIGHT_POSITION = WatermarkPositions.TopRight;
    static WATERMARK_BOTTOM_LEFT_POSITION = WatermarkPositions.BottomLeft;
    static WATERMARK_BOTTOM_RIGHT_POSITION = WatermarkPositions.BottomRight;
    static WATERMARK_BOTTOM_CENTER_POSITION = WatermarkPositions.BottomCenter;
    static WATERMARK_CENTER_POSITION = WatermarkPositions.Center;
    static WATERMARK_TOP_CENTER_POSITION = WatermarkPositions.TopCenter;
    static WATERMARK_CENTER_LEFT_POSITION = WatermarkPositions.CenterLeft;
    static WATERMARK_CENTER_RIGHT_POSITION = WatermarkPositions.CenterRight;
    static WATERMARK_POSITIONS = [
        Config.WATERMARK_TOP_LEFT_POSITION,
        Config.WATERMARK_TOP_RIGHT_POSITION,
        Config.WATERMARK_BOTTOM_LEFT_POSITION,
        Config.WATERMARK_BOTTOM_RIGHT_POSITION,
        Config.WATERMARK_BOTTOM_CENTER_POSITION,
        Config.WATERMARK_CENTER_POSITION,
        Config.WATERMARK_TOP_CENTER_POSITION,
        Config.WATERMARK_CENTER_LEFT_POSITION,
        Config.WATERMARK_CENTER_RIGHT_POSITION,
    ];

    /* text directions */
    static LEFT_TO_RIGHT_TEXT_DIRECTION = TextDirection.LTR;
    static RIGHT_TO_LEFT_TEXT_DIRECTION = TextDirection.RTL;
    static TEXT_DIRECTIONS = [
        Config.LEFT_TO_RIGHT_TEXT_DIRECTION,
        Config.RIGHT_TO_LEFT_TEXT_DIRECTION,
    ];

    /* animation direction */
    static LEFT_ANIMATION_DIRECTION = 'left' as const;
    static RIGHT_ANIMATION_DIRECTION = 'right' as const;
    static ZIG_ZAG_LEFT_RIGHT_DIRECTION = 'zigZagLeftRight' as const;
    static ZIG_ZAG_RIGHT_LEFT_DIRECTION = 'zigZagRightLeft' as const;
    static CENTER_ANIMATION_DIRECTION = 'center' as const;
    static TOP_ANIMATION_DIRECTION = 'top' as const;
    static BOTTOM_ANIMATION_DIRECTION = 'bottom' as const;
    static ZIG_ZAG_TOP_BOTTOM_DIRECTION = 'zigZagTopBottom' as const;
    static ZIG_ZAG_BOTTOM_TOP_DIRECTION = 'zigZagBottomTop' as const;

    static ALL_ANIMATION_IN_X_DIRECTION = [
        Config.LEFT_ANIMATION_DIRECTION,
        Config.CENTER_ANIMATION_DIRECTION,
        Config.RIGHT_ANIMATION_DIRECTION,
        Config.ZIG_ZAG_LEFT_RIGHT_DIRECTION,
        Config.ZIG_ZAG_RIGHT_LEFT_DIRECTION,
    ] as const;
    static ALL_ANIMATION_IN_Y_DIRECTION = [
        Config.TOP_ANIMATION_DIRECTION,
        Config.CENTER_ANIMATION_DIRECTION,
        Config.BOTTOM_ANIMATION_DIRECTION,
        Config.ZIG_ZAG_TOP_BOTTOM_DIRECTION,
        Config.ZIG_ZAG_BOTTOM_TOP_DIRECTION,
    ] as const;

    /* video height */
    static SUPPORTED_VIDEO_HEIGHTS = _SUPPORTED_VIDEO_HEIGHTS;

    /* slide base dimensions */
    static BASE_SLIDE_DIMENSIONS = _BASE_SLIDE_DIMENSIONS;

    /* animation */
    static readonly START_ANIMATION_CURVE_ANCHOR = 'start';
    static readonly MIDDLE_ANIMATION_CURVE_ANCHOR = 'middle';
    static readonly END_ANIMATION_CURVE_ANCHOR = 'end';
    static readonly NONE_ANIMATION_CURVE_ANCHOR = 'none';
    static ALL_ANIMATION_CURVE_ANCHOR = 'all';
    static ANIMATION_CURVE_ANCHORS = [
        Config.START_ANIMATION_CURVE_ANCHOR,
        Config.MIDDLE_ANIMATION_CURVE_ANCHOR,
        Config.END_ANIMATION_CURVE_ANCHOR,
        Config.NONE_ANIMATION_CURVE_ANCHOR,
    ];

    // Valid for both when in a Scene or Subscene
    static ANIMATE_OUT_BEHAVIOUR_ALWAYS = 'always';
    // Valid when asset in in a Subscene
    static ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING = 'when_preceding';
    // Valid when asset is in a Scene
    static ANIMATE_OUT_BEHAVIOUR_NEVER = 'never';
    static ANIMATE_OUT_BEHAVIOURS = [
        Config.ANIMATE_OUT_BEHAVIOUR_ALWAYS,
        Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING,
        Config.ANIMATE_OUT_BEHAVIOUR_NEVER,
    ];

    /* defaults */
    static DEFAULT_OVERLAY_RGBA = [0, 0, 0, 0];
    static DEFAULT_READING_SPEED = 15;
    static TRANSFORM = {
        X_SCALE: 0,
        X_SKEW: 1,
        X_TRANSLATION: 2,
        Y_SCALE: 3,
        Y_SKEW: 4,
        Y_TRANSLATION: 5,
    };

    /* media */
    static INVISIBLE_IMAGE = 'https://storage.googleapis.com/lumen5-prod-images/invisible.png';
    static INVISIBLE_IMAGE_SOURCE_DATA = {
        type: Config.IMAGE_TYPE_BACKGROUND,
        uuid: '35394c48-601f-6adf-8f78-9fd309d87f79',
        source: null as number | null,
        source_id: null as string | null,
        license: Config.CC0_LICENSE,
        attribution: null as string | null,
        is_editorial: false,
        width: 500,
        height: 500,
        thumbnail_url: Config.INVISIBLE_IMAGE,
        preview_url: Config.INVISIBLE_IMAGE,
        render_url: Config.INVISIBLE_IMAGE,
        title: '',
    };

    static INVISIBLE_LOTTIE = {
        uuid: '22831e1c-c3c3-4831-a79b-7aaa7736405b',
        animated: false,
        url: 'https://storage.googleapis.com/lumen5-prod-lottie/icons/transparent_lottie.json',
        source: 30, // SOURCE_LUMEN5_ICONS_PREMIUM
        source_id: 'transparent',
        license: Config.CC0_LICENSE as string,
    };

    static DUMMY_IMAGE = 'https://storage.googleapis.com/lumen5-site-images/dummy-image.png';
    static DUMMY_IMAGE_SOURCE_DATA = {
        type: Config.IMAGE_TYPE_BACKGROUND,
        uuid: '8749653A-2CCB-4A67-B8DE-CC4A443C7FCF',
        source: null as number | null,
        source_id: null as string | null,
        license: Config.CC0_LICENSE,
        attribution: null as string | null,
        is_editorial: false,
        width: 640,
        height: 480,
        thumbnail_url: Config.DUMMY_IMAGE,
        preview_url: Config.DUMMY_IMAGE,
        render_url: Config.DUMMY_IMAGE,
        title: '',
    };

    static WHITE_IMAGE = 'https://storage.googleapis.com/lumen5-prod-images/white.jpg';
    static WHITE_IMAGE_SOURCE_DATA = {
        type: Config.IMAGE_TYPE_BACKGROUND,
        uuid: '8f0df764-cfa8-4e04-8b21-c0a8c9431cfa',
        source: 14,
        source_id: 'white',
        license: Config.CC0_LICENSE,
        attribution: null as string | null,
        is_editorial: false,
        width: 640,
        height: 480,
        thumbnail_url: Config.WHITE_IMAGE,
        preview_url: Config.WHITE_IMAGE,
        render_url: Config.WHITE_IMAGE,
        title: '',
    };

    static BLACK_IMAGE = 'https://storage.googleapis.com/lumen5-prod-images/black.jpg';
    static BLACK_IMAGE_SOURCE_DATA = {
        type: Config.IMAGE_TYPE_BACKGROUND,
        uuid: '4c5f5939-d9b0-4b8e-b962-efd2a0c090ae',
        source: 14,
        source_id: 'black',
        license: Config.CC0_LICENSE,
        attribution: null as string | null,
        is_editorial: false,
        width: 640,
        height: 480,
        thumbnail_url: Config.BLACK_IMAGE,
        preview_url: Config.BLACK_IMAGE,
        render_url: Config.BLACK_IMAGE,
        title: '',
    };

    static BLACK_MP4_URL = 'https://storage.googleapis.com/lumen5-prod-video/black.mp4';

    /* branding slide assets */
    static BRANDING_LANDSCAPE_THUMBNAIL_VIDEO =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/landscape-thumbnail-250x156.mp4';
    static BRANDING_LANDSCAPE_PREVIEW_VIDEO =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/landscape-preview-640x360.mp4';
    static BRANDING_LANDSCAPE_RENDER_VIDEO =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/landscape-render-1920x1080.mp4';
    static BRANDING_LANDSCAPE_POSTER =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/landscape-poster-640x360.png';

    static BRANDING_SQUARE_THUMBNAIL_VIDEO =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/square-thumbnail-280x280.mp4';
    static BRANDING_SQUARE_PREVIEW_VIDEO =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/square-preview-540x540.mp4';
    static BRANDING_SQUARE_RENDER_VIDEO =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/square-render-1080x1080.mp4';
    static BRANDING_SQUARE_POSTER =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/square-poster-540x540.png';

    static BRANDING_VERTICAL_THUMBNAIL_VIDEO =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/vertical-thumbnail-156x280.mp4';
    static BRANDING_VERTICAL_PREVIEW_VIDEO =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/vertical-preview-540x960.mp4';
    static BRANDING_VERTICAL_RENDER_VIDEO =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/vertical-render-1080x1920.mp4';
    static BRANDING_VERTICAL_POSTER =
        'https://storage.googleapis.com/lumen5-prod-video/branded-outros/vertical-poster-540x960.png';

    static LOGO_PLACEHOLDER_THUMBNAIL_IMAGE =
        'https://storage.googleapis.com/lumen5-prod-images/logo-placeholder_256.png';
    static LOGO_PLACEHOLDER_PREVIEW_IMAGE =
        'https://storage.googleapis.com/lumen5-prod-images/logo-placeholder_512.png';
    static LOGO_PLACEHOLDER_RENDER_IMAGE =
        'https://storage.googleapis.com/lumen5-prod-images/logo-placeholder_1024.png';

    static BRAND_PROFILE_PLACEHOLDER_IMAGE =
        'https://storage.googleapis.com/lumen5-site-images/profile-placeholder.png';

    /* subscene animation */
    static MAX_ANIMATION_DURATION_PERCENTAGE = 0.5;

    /* asset group animation */
    static AT_THE_SAME_TIME_ANIMATION_MODE = 'AT_THE_SAME_TIME' as const;
    static SEQUENTIAL_TOP_DOWN_ANIMATION_MODE = 'SEQUENTIAL_TOP_DOWN' as const;
    static SEQUENTIAL_BOTTOM_UP_ANIMATION_MODE = 'SEQUENTIAL_BOTTOM_UP' as const;
    static ASSET_GROUP_ANIMATION_MODES = [
        Config.AT_THE_SAME_TIME_ANIMATION_MODE,
        Config.SEQUENTIAL_TOP_DOWN_ANIMATION_MODE,
        Config.SEQUENTIAL_BOTTOM_UP_ANIMATION_MODE,
    ] as const;

    /*************
     * WORD STYLES
     *************/

    /**
     * Possible horizontal text alignment options for {@link Luminary.WordStyle}.
     */
    static TEXT_ALIGN_HORIZONTAL = {
        LEFT: 'left',
        CENTER: 'center',
        RIGHT: 'right',
    };

    /**
     * Possible text direction options for {@link Luminary.WordStyle}.
     */
    static TEXT_DIRECTION = {
        /**
         * Text from left to right
         */
        LTR: 'ltr',
        /**
         * Text from right to left
         */
        RTL: 'rtl',
    };

    /* AUDIO TRANSITION */
    static AUDIO_TRANSITION_DURATION = 2000;

    /* template status */
    static TEMPLATE_STATUS_DRAFT = 'draft';
    static TEMPLATE_STATUS_ACTIVE = 'active';
    static TEMPLATE_STATUS_ARCHIVED = 'archived';

    static ALL_TEMPLATE_STATUS = [
        Config.TEMPLATE_STATUS_DRAFT,
        Config.TEMPLATE_STATUS_ACTIVE,
        Config.TEMPLATE_STATUS_ARCHIVED,
    ];

    /**
     * Different modes for how audio works within a luminary instance
     */
    static AUDIO_MODES = {
        /**
         * When luminary is being used interactively and the audio needs to be in sync in realtime with the video
         */
        INTERACTIVE: 'interactive',
        /**
         * only rendering the audio, so let it render as fast as possible
         */
        RENDER: 'render',
    };

    // Render settings for Audio
    static AUDIO_RENDER_SAMPLE_RATE = 48000;
    static AUDIO_RENDER_NUMBER_CHANNELS = 2;

    // Timeline types
    static BASE_TIMELINE_TYPE = 'BaseTimeline' as const;
    static CONCURRENT_TIMELINE_TYPE = 'ConcurrentTimeline' as const;
    static SEQUENTIAL_TIMELINE_TYPE = 'SequentialTimeline' as const;
    static TICKER_TIMELINE_TYPE = 'TickerTimeline' as const;

    // asset types, useful for passing into `getAssetsByType`
    static COLOR_ASSET_TYPE = 'ColorAsset' as const;
    static CAPTION_ASSET_TYPE = 'CanvasCaptionAsset' as const;
    static FANCY_CAPTION_ASSET_TYPE = 'CanvasFancyCaptionAsset' as const;
    static TEXT_ASSET_TYPE = 'CanvasTextAsset' as const;
    static MEDIA_ASSET_TYPE = 'TimeAwareMediaAsset' as const;
    static GROUP_ASSET_TYPE = 'AssetGroup' as const;
    static LOTTIE_ASSET_TYPE = 'LottieAsset' as const;
    static ICON_ASSET_TYPE = 'IconAsset' as const;
    static CIRCLE_ASSET_TYPE = 'CircleAsset' as const;
    static GRADIENT_ASSET_TYPE = 'GradientAsset' as const;
    static RECTANGLE_ASSET_TYPE = 'RectangleAsset' as const;
    static CHART_ASSET_TYPE = 'ChartAsset' as const;
    static HTML_ASSET_TYPE = 'HtmlAsset' as const;
    static COMPOSITING_ASSET_TYPE = 'CompositingAsset' as const;
    static AUDIO_ASSET_TYPE = 'AudioAsset' as const;
    static SCENE_GROUP_TYPE = 'SceneGroup' as const;

    static ALL_SLIDE_CHILDREN_TYPES = [
        Config.COLOR_ASSET_TYPE,
        Config.TEXT_ASSET_TYPE,
        Config.MEDIA_ASSET_TYPE,
        Config.GROUP_ASSET_TYPE,
        Config.LOTTIE_ASSET_TYPE,
        Config.ICON_ASSET_TYPE,
        Config.FANCY_CAPTION_ASSET_TYPE,
        Config.CHART_ASSET_TYPE,
        Config.HTML_ASSET_TYPE,
    ];

    static ALL_ASSET_TYPES = [
        Config.TEXT_ASSET_TYPE,
        Config.MEDIA_ASSET_TYPE,
        Config.GROUP_ASSET_TYPE,
        Config.COLOR_ASSET_TYPE,
        Config.CIRCLE_ASSET_TYPE,
        Config.GRADIENT_ASSET_TYPE,
        Config.RECTANGLE_ASSET_TYPE,
        Config.CHART_ASSET_TYPE,
        Config.LOTTIE_ASSET_TYPE,
        Config.ICON_ASSET_TYPE,
        Config.CAPTION_ASSET_TYPE,
        Config.FANCY_CAPTION_ASSET_TYPE,
        Config.COMPOSITING_ASSET_TYPE,
        Config.HTML_ASSET_TYPE,
    ];

    // COLOR/RECTANGLE/CIRCLE/GRADIENT are permitted so addSticker() accepts the background/shape
    // duplicates a match-cut creates. Transient duplicates are excluded from saves by isMatchCutDuplicate, not by type.
    static ALL_STICKER_ASSET_TYPES = [
        Config.ICON_ASSET_TYPE,
        Config.MEDIA_ASSET_TYPE,
        Config.TEXT_ASSET_TYPE,
        Config.GROUP_ASSET_TYPE,
        Config.CHART_ASSET_TYPE,
        Config.COLOR_ASSET_TYPE,
        Config.RECTANGLE_ASSET_TYPE,
        Config.CIRCLE_ASSET_TYPE,
        Config.GRADIENT_ASSET_TYPE,
    ];

    // Technique IDs for Scene.matchCutTechnique. 'none' = no match-cut (plain morph, scene sequence, or none).
    static MATCH_CUT_NONE = 'none' as const;
    static MATCH_CUT_TEXT = 'text' as const;
    static ALL_MATCH_CUT_TECHNIQUES = [Config.MATCH_CUT_NONE, Config.MATCH_CUT_TEXT] as const;

    static CIRCLE_MASK_TYPE = 'CircleMask' as const;
    static POLYGON_MASK_TYPE = 'PolygonMask' as const;

    // Filters
    static BLUR_FILTER = 'BlurFilter' as const;
    static ADJUSTMENT_FILTER = 'AdjustmentFilter' as const;
    static HSL_ADJUSTMENT_FILTER = 'HslAdjustmentFilter' as const;
    static COLOR_CHANNEL_MAP_FILTER = 'ColorChannelMapFilter' as const;
    static GREYSCALE_FILTER = 'GreyscaleFilter' as const;
    static DUO_TONE_FILTER = 'DuoToneFilter' as const;
    static OLD_FILM_FILTER = 'OldFilmFilter' as const;
    static COLOR_GRADING_FILTER = 'ColorGradingFilter' as const;
    static ALL_EXTERNAL_MEDIA_FILTERS = [Config.COLOR_CHANNEL_MAP_FILTER, Config.DUO_TONE_FILTER];

    static ALL_FILTERS = [
        Config.ADJUSTMENT_FILTER,
        Config.BLUR_FILTER,
        Config.GREYSCALE_FILTER,
        Config.HSL_ADJUSTMENT_FILTER,
        Config.OLD_FILM_FILTER,
        Config.COLOR_GRADING_FILTER,
        ...Config.ALL_EXTERNAL_MEDIA_FILTERS,
    ];
    // Scene design type => category mapping

    static CATEGORY_ORDINARY = 'ordinary' as const;
    static CATEGORY_OUTRO = 'outro' as const;
    static CATEGORY_BRAND = 'brand' as const;
    static CATEGORY_TALKING_HEAD = 'talking_head' as const;
    static CATEGORY_TALKING_HEAD_INSERT = 'talking_head_insert' as const;
    static CATEGORY_VOICEOVER = 'voiceover' as const;
    static CATEGORY_CAPTION = 'caption' as const;
    static CATEGORY_COMMON = 'common' as const;
    static ALL_CATEGORIES = [
        Config.CATEGORY_ORDINARY,
        Config.CATEGORY_OUTRO,
        Config.CATEGORY_BRAND,
        Config.CATEGORY_TALKING_HEAD,
        Config.CATEGORY_TALKING_HEAD_INSERT,
        Config.CATEGORY_VOICEOVER,
        Config.CATEGORY_CAPTION,
        Config.CATEGORY_COMMON,
    ];

    /**
     * Dynamic graphic style alignment
     */

    static DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL = {
        LEFT: 'left',
        CENTER: 'center',
        RIGHT: 'right',
    };
    static DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL_OPTIONS = [
        Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.LEFT,
        Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.CENTER,
        Config.DYNAMIC_GRAPHIC_ALIGN_HORIZONTAL.RIGHT,
    ];

    static DYNAMIC_GRAPHIC_ALIGN_VERTICAL = {
        TOP: 'top',
        CENTER: 'center',
        BOTTOM: 'bottom',
        BASELINE: 'baseline',
    };
    static DYNAMIC_GRAPHIC_ALIGN_VERTICAL_OPTIONS = [
        Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.TOP,
        Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.CENTER,
        Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.BOTTOM,
        Config.DYNAMIC_GRAPHIC_ALIGN_VERTICAL.BASELINE,
    ];

    static MEDIA_MIN_SCALE = 0;
    static MEDIA_MAX_SCALE = Number.POSITIVE_INFINITY;
    static MEDIA_MIN_SCALE_X = this.MEDIA_MIN_SCALE;
    static MEDIA_MAX_SCALE_X = this.MEDIA_MAX_SCALE;
    static MEDIA_MIN_SCALE_Y = this.MEDIA_MIN_SCALE;
    static MEDIA_MAX_SCALE_Y = this.MEDIA_MAX_SCALE;

    /**
     * Due to frame/time calculations we sometimes want to round a time value to a specific
     * precision. For example, a time of 3.99999999s should be equivalent to 4s in terms of
     * which frame is shown on a video. This constant defines the number of digits after the
     * decimal we round time to (assuming units of seconds).
     *
     * Note that ffmpeg seems to use microseconds as its precision cutoff, so it's good to match
     * that for other node related code.
     */
    static VIDEO_TIME_PRECISION = 6;
    /**
     * Refer to VIDEO_TIME_PRECISION. This is a convenience definition for working with time
     * values in milliseconds.
     */
    static VIDEO_TIME_PRECISION_MS = Config.VIDEO_TIME_PRECISION - 3;

    static SeekMode = SeekMode;

    static AnimationCreationSource = AnimationCreationSource;

    // Asset Type Guards
    /** @internal **/
    static isAsset = (asset: unknown): asset is Asset =>
        !!asset &&
        typeof asset === 'object' &&
        '_classType' in asset &&
        typeof asset._classType === 'string' &&
        Config.ALL_ASSET_TYPES.includes(
            asset._classType as (typeof Config.ALL_ASSET_TYPES)[number],
        );
    static isCircleAsset = (asset: Asset): asset is CircleAsset =>
        asset._classType === Config.CIRCLE_ASSET_TYPE;
    static isColorAsset = (asset: Asset): asset is ColorAsset =>
        asset._classType === Config.COLOR_ASSET_TYPE;
    static isGradientAsset = (asset: Asset): asset is GradientAsset =>
        asset._classType === Config.GRADIENT_ASSET_TYPE;
    static isChartAsset = (asset: Asset): asset is ChartAsset =>
        asset._classType === Config.CHART_ASSET_TYPE;
    static isHtmlAsset = (asset: Asset): asset is HtmlAsset =>
        asset._classType === Config.HTML_ASSET_TYPE;
    static isIconAsset = (asset: Asset): asset is IconAsset =>
        asset._classType === Config.ICON_ASSET_TYPE;
    static isLottieAsset = (asset: Asset): asset is LottieAsset =>
        asset._classType === Config.LOTTIE_ASSET_TYPE;
    static isRectangleAsset = (asset: Asset): asset is RectangleAsset =>
        asset._classType === Config.RECTANGLE_ASSET_TYPE;
    static isAssetGroup = (asset: Asset): asset is AssetGroup =>
        asset._classType === Config.GROUP_ASSET_TYPE;
    static isMediaAsset = (asset: Asset): asset is TimeAwareMediaAsset =>
        asset._classType === Config.MEDIA_ASSET_TYPE;
    static isAudioAsset = (asset: Asset | AudioAsset): asset is AudioAsset =>
        asset._classType === Config.AUDIO_ASSET_TYPE;
    static isTextAsset = (asset: Asset): asset is TextAsset =>
        asset._classType === Config.TEXT_ASSET_TYPE;
    static isCaptionAsset = (asset: Asset): asset is CaptionAsset =>
        asset._classType === Config.CAPTION_ASSET_TYPE;
    static isFancyCaptionAsset = (asset: Asset): asset is FancyCaptionAsset =>
        asset._classType === Config.FANCY_CAPTION_ASSET_TYPE;
    static isCompositingAsset = (asset: Asset): asset is CompositingAsset =>
        asset._classType === 'CompositingAsset';

    static isCircleMask = (mask: Container | MaskData): mask is CircleMask =>
        mask && (mask as StencilMask)._classType === Config.CIRCLE_MASK_TYPE;
    static isPolygonMask = (mask: Container | MaskData): mask is PolygonMask =>
        mask && (mask as StencilMask)._classType === Config.POLYGON_MASK_TYPE;
    static isStencilMask = (mask: Container | MaskData): mask is StencilMask =>
        this.isCircleMask(mask) || this.isPolygonMask(mask);

    // A STICKER-created ColorAsset/RectangleAsset/CircleAsset/GradientAsset is recognized here at runtime
    // (so it is enumerated/named/excluded as a sticker) even though none are part of the StickerAsset type
    // — see the note on StickerAsset.
    static isStickerAsset = (asset: Asset): asset is StickerAsset => {
        return (
            (Config.isIconAsset(asset) ||
                Config.isMediaAsset(asset) ||
                Config.isTextAsset(asset) ||
                Config.isAssetGroup(asset) ||
                Config.isChartAsset(asset) ||
                Config.isColorAsset(asset) ||
                Config.isRectangleAsset(asset) ||
                Config.isCircleAsset(asset) ||
                Config.isGradientAsset(asset)) &&
            asset.createdFrom === AssetCreatedFrom.STICKER
        );
    };

    static isAnyTextAsset = (asset: Asset): asset is TextAsset => {
        if (Config.isTextAsset(asset)) {
            return true;
        }
        if (Config.isAnyCanvasTextAsset(asset)) {
            return true;
        }
        return false;
    };

    /** Returns true if the given asset is using a canvas text rendering engine */
    static isAnyCanvasTextAsset = (
        asset: Asset,
    ): asset is TextAsset | FancyCaptionAsset | CaptionAsset => {
        if (Config.isTextAsset(asset)) {
            return true;
        }
        if (Config.isFancyCaptionAsset(asset)) {
            return true;
        }
        if (Config.isCaptionAsset(asset)) {
            return true;
        }
        return false;
    };

    // Filter type guards
    static isColorChannelMapFilter = (filter: Filter): filter is ColorChannelMapFilter =>
        filter.classType === Config.COLOR_CHANNEL_MAP_FILTER;
    static isDuoToneFilter = (filter: Filter): filter is DuoToneFilter =>
        filter.classType === Config.DUO_TONE_FILTER;

    // Misc type guards
    static isScene = (item: unknown): item is Scene =>
        !!item && typeof item === 'object' && '_classType' in item && item._classType === 'Scene';
    static isSubscene = (item: unknown): item is Subscene =>
        !!item &&
        typeof item === 'object' &&
        '_classType' in item &&
        item._classType === 'Subscene';
    static isTextAssetStyle = (item: unknown): item is TextAssetStyle =>
        !!item &&
        typeof item === 'object' &&
        '_classType' in item &&
        item._classType === 'CanvasTextAssetStyle';
    static isSceneGroup = (item: unknown): item is SceneGroup =>
        !!item &&
        typeof item === 'object' &&
        '_classType' in item &&
        item._classType === Config.SCENE_GROUP_TYPE;

    // virtual designs
    static SCENE_MODIFICATION_KEY = '__scene__';

    static getSupportedAspectRatios = (templateBundle: TemplateBundle): AspectRatio[] => {
        const firstSceneDesign = Object.values(templateBundle.sceneDesigns)[0];

        if (!firstSceneDesign) {
            throw new Error('TemplateBundle does not contain any scene designs');
        }

        return Config.ALL_ASPECT_RATIOS.filter((aspectRatio) => !!firstSceneDesign[aspectRatio]);
    };

    static isTemplateBundleValid = (templateBundle: TemplateBundle): boolean => {
        return (
            !!templateBundle.templateData &&
            !!templateBundle.sceneDesigns &&
            Object.keys(templateBundle.sceneDesigns).length > 0
        );
    };

    static FRAME_SCENE_IMAGE_RENDER_MODE = SceneImageRenderMode.FRAME;
    static ALL_ASSETS_VISIBLE_SCENE_IMAGE_RENDER_MODE = SceneImageRenderMode.ALL_ASSETS_VISIBLE;
    static ALL_SCENE_IMAGE_RENDER_MODES = [
        Config.FRAME_SCENE_IMAGE_RENDER_MODE,
        Config.ALL_ASSETS_VISIBLE_SCENE_IMAGE_RENDER_MODE,
    ];
}
